import React, { useRef, useCallback } from 'react';
import DropdownList from 'react-widgets/DropdownList';
import './ComboSelector.scss';

export const TipoClave = {
  ID: 'id',
  CODIGO: 'codigo',
};

const ComboSelector = (props) => {
  const {
    id,
    lista,
    valorActual,
    campoClave,
    campoDescripcion,
    isCargando,
    estilo,
    onItemElegido: onItemElegidoProperty,
  } = props;

  // Tracks whether the user already made an explicit selection via click or Enter,
  // so that the focusout handler does not override it with a text-based match.
  const explicitSelectionRef = useRef(false);

  const onItemElegido = useCallback(
    (item) => {
      explicitSelectionRef.current = true;
      onItemElegidoProperty(item);
    },
    [onItemElegidoProperty]
  );

  const containerReference = useRef(null);

  const selectSingleMatch = useCallback(() => {
    // If the user already picked an item explicitly (click / Enter via onChange),
    // skip the text-based heuristic so we don't override the real selection.
    if (explicitSelectionRef.current) {
      explicitSelectionRef.current = false;
      return false;
    }
    try {
      let text = '';
      const element = containerReference.current;
      if (element) {
        // Prefer the visible search input used by react-widgets DropdownList
        let input = element.querySelector('input.rw-dropdownlist-search');
        if (!input) {
          // fallback to an input that is not aria-hidden
          input = [...element.querySelectorAll('input')].find(
            (index) => index.getAttribute('aria-hidden') !== 'true'
          );
        }
        if (input) {
          text = input.value.trim();
        } else {
          // fallback to displayed value span
          const span = element.querySelector('.rw-dropdown-list-value');
          text = span ? (span.textContent || '').trim() : (element.textContent || '').trim();
        }
      }
      if (!text || !Array.isArray(lista) || lista.length === 0) return false;

      // try exact match first
      const exact = lista.filter((it) => {
        const desc = campoDescripcion ? it[campoDescripcion] : it;
        if (desc === null || desc === undefined) return false;
        return String(desc).trim().toLowerCase() === text.toLowerCase();
      });
      if (exact.length > 0) {
        // if there is at least one exact match, pick the first
        onItemElegido(exact[0]);
        return true;
      }

      // fallback: if only one item contains the text, select it
      const contains = lista.filter((it) => {
        const desc = campoDescripcion ? it[campoDescripcion] : it;
        if (desc === null || desc === undefined) return false;
        return String(desc).toLowerCase().includes(text.toLowerCase());
      });
      if (contains.length > 0) {
        // if there is at least one partial match, pick the first (do not disturb while typing)
        onItemElegido(contains[0]);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }, [lista, campoDescripcion, onItemElegido]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab') {
        // try to select synchronously before focus moves
        selectSingleMatch();
      }
    },
    [selectSingleMatch]
  );

  const handleBlur = useCallback(
    (e) => {
      selectSingleMatch();
    },
    [selectSingleMatch]
  );

  return (
    <div ref={containerReference}>
      <DropdownList
        key={id}
        className={estilo}
        busy={isCargando}
        filter="contains"
        datakey={campoClave}
        textField={campoDescripcion}
        value={valorActual}
        data={lista}
        onChange={onItemElegido}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default ComboSelector;
